import {
    useLayoutEffect,
    useState
} from "react";
import {
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    CodeViewer,
    SelectBox,
    TextInput,
    Button,
    Header,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../../navigation/type";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import type {
    ITextInputProps
} from "ncore-ui-kit";
import {
    Home as HomeIcon
} from "lucide-react-native";

const TYPES: Array<ITextInputProps["type"]> = [
    "default",
    "danger",
    "warning",
    "question",
    "success",
    "info"
];
const VARIANTS: Array<ITextInputProps["variant"]> = [
    "text",
    "hidden"
];
const SPREAD_BEHAVIOURS: Array<ITextInputProps["spreadBehaviour"]> = [
    "baseline",
    "stretch",
    "free"
];

const TYPES_DATA = TYPES.map((item) => ({
    __title: item as string,
    __key: item as string
}));

const VARIANTS_DATA = VARIANTS.map((item) => ({
    __title: item as string,
    __key: item as string
}));

const TextInputPage = () => {
    const {
        radiuses,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const [
        typeIndex,
        setTypeIndex
    ] = useState<number>(0);

    const [
        variantIndex,
        setVariantIndex
    ] = useState<number>(0);

    const [
        spreadIndex,
        setSpreadIndex
    ] = useState<number>(0);

    const [
        title,
        setTitle
    ] = useState<string>("");

    const [
        isDisabled,
        setIsDisabled
    ] = useState<boolean>(false);

    const [
        showIcon,
        setShowIcon
    ] = useState<boolean>(false);

    const [
        isCleanEnabled,
        setIsCleanEnabled
    ] = useState<boolean>(true);

    const [
        isRequired,
        setIsRequired
    ] = useState<boolean>(false);

    const [
        isOptional,
        setIsOptional
    ] = useState<boolean>(false);

    const [
        val,
        setVal
    ] = useState<string>("");

    useLayoutEffect(() => {
        navigation.setOptions({
            header: () => <Header
                title={localize("textInput-title")}
                isWrapSafeareaContext={false}
                navigation={navigation}
                isGoBackEnable={true}
            />,
            headerShown: true
        });
    }, []);

    return <PageContainer
        scrollViewProps={{
            contentContainerStyle: stylesheet.scrollContent
        }}
        isWorkWithHeaderSpace={false}
        scrollViewStyle={[
            stylesheet.container
        ]}
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.contentContainer
            ]}
        >
            <Text
                style={stylesheet.descText}
                variant="bodyMediumSize"
                color="mid"
            >
                {localize("textInput-desc")}
            </Text>
            <View
                style={[
                    stylesheet.previewContainer,
                    {
                        borderColor: colors.content.border.subtle,
                        marginBottom: spaces.spacingMd,
                        borderRadius: radiuses.form,
                        borderWidth: borders.line,
                        padding: spaces.spacingMd
                    }
                ]}
            >
                <TextInput
                    title={title || localize("textInput-exampleTitle")}
                    spreadBehaviour={SPREAD_BEHAVIOURS[spreadIndex]}
                    placeholder={localize("textInput-placeholder")}
                    icon={
                        showIcon
                            ? ({
                                color,
                                size
                            }) => <HomeIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />

                            : undefined
                    }
                    variant={VARIANTS[variantIndex]}
                    isCleanEnabled={isCleanEnabled}
                    type={TYPES[typeIndex]}
                    isDisabled={isDisabled}
                    isRequired={isRequired}
                    isOptional={isOptional}
                    onChangeText={setVal}
                    value={val}
                />
            </View>
            <View
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                <Text
                    style={{
                        marginBottom: spaces.spacingSm
                    }}
                    variant="headlineSmallSize"
                >
                    {localize("usage")}
                </Text>
                <CodeViewer
                    code={"import {\n    TextInput\n} from \"ncore-ui-kit\";\n\n<TextInput\n    title=\"Title\"\n    placeholder=\"Placeholder\"\n    value={value}\n    onChangeText={setValue}\n/>"}
                    language="tsx"
                />
            </View>
            <TextInput
                placeholder={localize("textInput-enterTitle")}
                title={localize("textInput-titleLabel")}
                style={{
                    marginBottom: spaces.spacingMd
                }}
                spreadBehaviour="stretch"
                onChangeText={setTitle}
                value={title}
            />
            <View
                style={[
                    stylesheet.rowContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <SelectBox
                    onChange={(selectedItems) => {
                        if (selectedItems.length > 0) {
                            const index = TYPES.indexOf(selectedItems[0]!.__key as unknown as ITextInputProps["type"]);

                            if (index !== -1) setTypeIndex(index);
                        }
                    }}
                    title={localize("textInput-changeType")}
                    titleExtractor={(item) => item.__title}
                    keyExtractor={(item) => item.__key}
                    initialSelectedItems={

                        TYPES_DATA[typeIndex]

                            ? [
                                TYPES_DATA[typeIndex]!
                            ]

                            : []

                    }
                    spreadBehaviour="free"
                    data={TYPES_DATA}
                />
                <SelectBox
                    onChange={(selectedItems) => {
                        if (selectedItems.length > 0) {
                            const index = VARIANTS.indexOf(selectedItems[0]!.__key as unknown as ITextInputProps["variant"]);

                            if (index !== -1) setVariantIndex(index);
                        }
                    }}
                    title={localize("textInput-changeVariant")}
                    initialSelectedItems={

                        VARIANTS_DATA[variantIndex]

                            ? [
                                VARIANTS_DATA[variantIndex]!
                            ]

                            : []

                    }
                    titleExtractor={(item) => item.__title}
                    keyExtractor={(item) => item.__key}
                    spreadBehaviour="free"
                    data={VARIANTS_DATA}
                />
                <Button
                    onPress={() => {
                        setSpreadIndex((prev) => prev + 1 > SPREAD_BEHAVIOURS.length - 1 ? 0 : prev + 1);
                    }}
                    title={`${localize("textInput-changeSpread")}: ${SPREAD_BEHAVIOURS[spreadIndex]}`}
                    spreadBehaviour="free"
                />
                <View
                    style={stylesheet.switchesContainer}
                >
                    <Switch
                        title={localize("textInput-toggleIcon")}
                        onPress={() => setShowIcon(!showIcon)}
                        spreadBehaviour="free"
                        isActive={showIcon}
                    />
                    <Switch
                        title={localize("textInput-toggleDisabled")}
                        onPress={() => setIsDisabled(!isDisabled)}
                        spreadBehaviour="free"
                        isActive={isDisabled}
                    />
                    <Switch
                        onPress={() => setIsCleanEnabled(!isCleanEnabled)}
                        title={localize("textInput-toggleClean")}
                        isActive={isCleanEnabled}
                        spreadBehaviour="free"
                    />
                    <Switch
                        title={localize("textInput-toggleRequired")}
                        onPress={() => setIsRequired(!isRequired)}
                        spreadBehaviour="free"
                        isActive={isRequired}
                    />
                    <Switch
                        title={localize("textInput-toggleOptional")}
                        onPress={() => setIsOptional(!isOptional)}
                        spreadBehaviour="free"
                        isActive={isOptional}
                    />
                </View>
            </View>
        </View>
    </PageContainer>;
};
export default TextInputPage;
